import { C } from '../data/constants.js';

// ─────────────────────────────────────────────────────────────────────────────
// Boss System — Shatter-Yo
//
// Four unique bosses, one per sector. Each has 3 HP-based phases with
// escalating mechanics. All coordinates are in the 400×700 logical canvas.
//
// Factory:  createBoss(sectorIndex)  →  BossBase subclass instance
// ─────────────────────────────────────────────────────────────────────────────

const TWO_PI = Math.PI * 2;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Distance² between two points (avoids sqrt). */
function dist2(ax, ay, bx, by) {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

/** Circle-circle overlap test. */
function circlesOverlap(ax, ay, ar, bx, by, br) {
  const rSum = ar + br;
  return dist2(ax, ay, bx, by) < rSum * rSum;
}

/** Reflect a velocity vector off a surface with a given normal (nx, ny). */
function reflect(vx, vy, nx, ny) {
  const dot = vx * nx + vy * ny;
  return { vx: vx - 2 * dot * nx, vy: vy - 2 * dot * ny };
}

/** Clamp value between min and max. */
function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

// ─── Base Boss ───────────────────────────────────────────────────────────────

class BossBase {
  /**
   * @param {string} name  – Display name rendered above HP bar.
   * @param {number} maxHp – Total hit points.
   */
  constructor(name, maxHp) {
    this.name = name;
    this.hp = maxHp;
    this.maxHp = maxHp;
    this.alive = true;
    this.phase = 1;
    this.phaseTimer = 0;      // general-purpose timer (ms)
    this.projectiles = [];    // {x, y, vx, vy, w, h, alive, color}
    this.shields = [];        // {x, y, w, h, angle, active}
    this.flashTimer = 0;      // frames of white flash remaining
    this.defeated = false;
    this.elapsed = 0;         // total elapsed ms since spawn
  }

  /* Override in subclasses */
  update(dtMs, balls, paddle) {}
  render(ctx) {}
  checkBallHit(ball) { return false; }

  /** Return current phase (1-3) based on remaining HP percentage. */
  getPhase() {
    const pct = this.hp / this.maxHp;
    if (pct > 0.7) return 1;
    if (pct > 0.3) return 2;
    return 3;
  }

  /** Apply damage and handle phase transitions / defeat. */
  takeDamage(amount) {
    if (!this.alive) return;
    this.hp -= amount;
    this.flashTimer = 5;

    const newPhase = this.getPhase();
    if (newPhase !== this.phase) {
      this.phase = newPhase;
      this.onPhaseChange(newPhase);
    }
    if (this.hp <= 0) {
      this.hp = 0;
      this.alive = false;
      this.defeated = true;
    }
  }

  /** Override for phase-transition effects (sound, visual burst, etc.). */
  onPhaseChange(newPhase) {}

  // ── HP Bar ─────────────────────────────────────────────────────────────

  renderHPBar(ctx) {
    const barW = 200;
    const barH = 6;
    const barX = (C.CANVAS_W - barW) / 2;
    const barY = C.HUD_HEIGHT + 10;
    const pct = this.hp / this.maxHp;

    // Background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(barX - 1, barY - 1, barW + 2, barH + 2);

    // Health (green → yellow → red)
    let color = '#00ff44';
    if (pct < 0.6) color = '#ffaa00';
    if (pct < 0.3) color = '#ff2222';
    ctx.fillStyle = color;
    ctx.shadowColor = color;
    ctx.shadowBlur = 4;
    ctx.fillRect(barX, barY, barW * pct, barH);
    ctx.shadowBlur = 0;

    // Boss name label
    ctx.font = '10px monospace';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText(this.name, C.CANVAS_W / 2, barY - 3);
  }

  // ── Projectile helpers ─────────────────────────────────────────────────

  updateProjectiles() {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i];
      p.x += p.vx;
      p.y += p.vy;
      // Cull off-screen
      if (p.y > C.CANVAS_H + 20 || p.y < -20 ||
          p.x < -20 || p.x > C.CANVAS_W + 20) {
        p.alive = false;
      }
      if (!p.alive) this.projectiles.splice(i, 1);
    }
  }

  renderProjectiles(ctx) {
    for (const p of this.projectiles) {
      const col = p.color || '#ff4444';
      ctx.fillStyle = col;
      ctx.shadowColor = col;
      ctx.shadowBlur = 4;
      ctx.fillRect(p.x - p.w / 2, p.y - p.h / 2, p.w, p.h);
    }
    ctx.shadowBlur = 0;
  }

  /** Flash overlay — call after main boss render. */
  renderFlash(ctx, x, y, radius) {
    if (this.flashTimer > 0) {
      ctx.globalAlpha = this.flashTimer / 5;
      ctx.beginPath();
      ctx.arc(x, y, radius + 4, 0, TWO_PI);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// BOSS 1 — THE HEX CORE  (Sector 1, HP 30)
//
// A rotating hexagonal core protected by orbiting shield panels.
// Two turrets fire downward. Ball must slip through shield gaps.
// ═════════════════════════════════════════════════════════════════════════════

class HexCore extends BossBase {
  constructor() {
    super('THE HEX CORE', 30);

    // Core geometry
    this.cx = C.CANVAS_W / 2;   // 200
    this.cy = 130;
    this.coreRadius = 35;
    this.coreAngle = 0;          // rotation of the hexagon itself

    // Shields — rebuilt on phase change
    this.shieldOrbitRadius = 55;
    this.shieldW = 40;
    this.shieldH = 8;
    this.shieldAngle = 0;        // current orbit angle
    this.shieldSpeed = 1;        // rad/s
    this.shieldCount = 2;
    this.shieldOpen = true;      // used in phase 3 pulsing
    this.shieldPulseTimer = 0;

    // Turret firing
    this.fireInterval = 3000;    // ms between bursts
    this.fireTimer = 0;
    this.shotsPerBurst = 1;
  }

  onPhaseChange(p) {
    if (p === 2) {
      this.shieldCount = 4;
      this.shieldSpeed = 1.5;
      this.fireInterval = 2500;
      this.shotsPerBurst = 2;
    } else if (p === 3) {
      this.shieldCount = 6;
      this.shieldSpeed = 2;
      this.fireInterval = 2000;
      this.shotsPerBurst = 3;
    }
  }

  // ── Update ────────────────────────────────────────────────────────────

  update(dtMs, balls, paddle) {
    if (!this.alive) return;
    this.elapsed += dtMs;
    if (this.flashTimer > 0) this.flashTimer--;

    const dtSec = dtMs / 1000;

    // Rotate shields
    this.shieldAngle += this.shieldSpeed * dtSec;

    // Core cosmetic rotation
    this.coreAngle += 0.3 * dtSec;

    // Phase-3 shield pulsing (open/close on 1.5s cycle)
    if (this.phase === 3) {
      this.shieldPulseTimer += dtMs;
      const cycle = 1500; // ms
      this.shieldOpen = (this.shieldPulseTimer % cycle) < cycle * 0.6;
    }

    // Build shield rects for collision use
    this._buildShields();

    // Turret firing
    this.fireTimer += dtMs;
    if (this.fireTimer >= this.fireInterval) {
      this.fireTimer = 0;
      this._fireTurrets();
    }

    this.updateProjectiles();

    // Ball vs shields / core
    for (const ball of balls) {
      if (!ball.alive || ball.attached) continue;
      this._handleBallCollisions(ball);
    }
  }

  /** Populate this.shields with current positions/angles. */
  _buildShields() {
    this.shields.length = 0;
    if (this.phase === 3 && !this.shieldOpen) return; // shields retracted
    for (let i = 0; i < this.shieldCount; i++) {
      const angle = this.shieldAngle + (TWO_PI / this.shieldCount) * i;
      this.shields.push({
        x: this.cx + Math.cos(angle) * this.shieldOrbitRadius,
        y: this.cy + Math.sin(angle) * this.shieldOrbitRadius,
        w: this.shieldW,
        h: this.shieldH,
        angle,
        active: true,
      });
    }
  }

  _fireTurrets() {
    // Two turret positions — left and right of core
    const turrets = [
      { x: this.cx - 30, y: this.cy + 10 },
      { x: this.cx + 30, y: this.cy + 10 },
    ];

    if (this.shotsPerBurst === 1) {
      // Alternate turrets
      const t = turrets[Math.floor(this.elapsed / this.fireInterval) % 2];
      this.projectiles.push(this._makeProjectile(t.x, t.y, 0, 1.5));
    } else if (this.shotsPerBurst === 2) {
      // Both turrets fire
      for (const t of turrets) {
        this.projectiles.push(this._makeProjectile(t.x, t.y, 0, 1.5));
      }
    } else {
      // 3-shot spread from each turret
      for (const t of turrets) {
        this.projectiles.push(this._makeProjectile(t.x, t.y, -0.4, 1.4));
        this.projectiles.push(this._makeProjectile(t.x, t.y, 0, 1.6));
        this.projectiles.push(this._makeProjectile(t.x, t.y, 0.4, 1.4));
      }
    }
  }

  _makeProjectile(x, y, vx, vy) {
    return { x, y, vx, vy, w: 4, h: 6, alive: true, color: '#00ffff' };
  }

  /** Ball ↔ shield reflection and core hit detection. */
  _handleBallCollisions(ball) {
    // Check shields first — if ball hits a shield, reflect it and skip core
    for (const s of this.shields) {
      if (!s.active) continue;
      // Treat shield as a rotated rectangle approximated by a circle check
      // with radius = half the diagonal
      const halfDiag = Math.sqrt(s.w * s.w + s.h * s.h) / 2;
      if (circlesOverlap(ball.x, ball.y, ball.radius, s.x, s.y, halfDiag * 0.6)) {
        // Reflect ball off the shield's tangent normal
        const nx = ball.x - s.x;
        const ny = ball.y - s.y;
        const len = Math.sqrt(nx * nx + ny * ny) || 1;
        const r = reflect(ball.vx, ball.vy, nx / len, ny / len);
        ball.vx = r.vx;
        ball.vy = r.vy;
        // Push ball out of collision
        ball.x = s.x + (nx / len) * (halfDiag * 0.6 + ball.radius + 1);
        ball.y = s.y + (ny / len) * (halfDiag * 0.6 + ball.radius + 1);
        return; // shield absorbed the hit
      }
    }
  }

  checkBallHit(ball) {
    if (!this.alive || ball.attached) return false;
    // Core hit — circle-circle
    if (circlesOverlap(ball.x, ball.y, ball.radius, this.cx, this.cy, this.coreRadius)) {
      // Make sure no shield is in the way (shields already handle reflection
      // in update, so if we reach here the ball slipped through)
      this.takeDamage(1);
      // Bounce ball away from core
      const nx = ball.x - this.cx;
      const ny = ball.y - this.cy;
      const len = Math.sqrt(nx * nx + ny * ny) || 1;
      const r = reflect(ball.vx, ball.vy, nx / len, ny / len);
      ball.vx = r.vx;
      ball.vy = r.vy;
      ball.x = this.cx + (nx / len) * (this.coreRadius + ball.radius + 2);
      ball.y = this.cy + (ny / len) * (this.coreRadius + ball.radius + 2);
      return true;
    }
    return false;
  }

  // ── Render ────────────────────────────────────────────────────────────

  render(ctx) {
    if (!this.alive) return;

    // Draw shields
    ctx.save();
    for (const s of this.shields) {
      ctx.save();
      ctx.translate(s.x, s.y);
      ctx.rotate(s.angle);
      ctx.fillStyle = '#00eeff';
      ctx.shadowColor = '#00eeff';
      ctx.shadowBlur = 6;
      ctx.fillRect(-s.w / 2, -s.h / 2, s.w, s.h);
      ctx.restore();
    }
    ctx.shadowBlur = 0;
    ctx.restore();

    // Draw hexagonal core
    ctx.save();
    ctx.translate(this.cx, this.cy);
    ctx.rotate(this.coreAngle);
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = (TWO_PI / 6) * i;
      const px = Math.cos(a) * this.coreRadius;
      const py = Math.sin(a) * this.coreRadius;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fillStyle = '#001822';
    ctx.fill();
    ctx.strokeStyle = '#00ffff';
    ctx.lineWidth = 2;
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 10;
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Inner glow circle
    ctx.beginPath();
    ctx.arc(0, 0, 12, 0, TWO_PI);
    ctx.fillStyle = 'rgba(0, 255, 255, 0.3)';
    ctx.fill();
    ctx.restore();

    // Turret indicators
    const turrets = [this.cx - 30, this.cx + 30];
    for (const tx of turrets) {
      ctx.beginPath();
      ctx.arc(tx, this.cy + 10, 4, 0, TWO_PI);
      ctx.fillStyle = '#00cccc';
      ctx.fill();
    }

    // Flash overlay
    this.renderFlash(ctx, this.cx, this.cy, this.coreRadius);

    this.renderProjectiles(ctx);
    this.renderHPBar(ctx);
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// BOSS 2 — THE CYBER KRAKEN  (Sector 2, HP 40)
//
// Mechanical eye with wavy tentacles. Eye blinks (invulnerable).
// Phase 2+ adds tentacle slams and gravity pulses.
// ═════════════════════════════════════════════════════════════════════════════

class CyberKraken extends BossBase {
  constructor() {
    super('THE CYBER KRAKEN', 40);

    // Eye
    this.eyeX = C.CANVAS_W / 2;
    this.eyeY = 100;
    this.eyeRadius = 30;
    this.eyeOpen = true;

    // Blink timing
    this.blinkCycle = 4000;    // ms between blinks
    this.blinkDuration = 1000; // ms eye stays closed
    this.blinkTimer = 0;

    // Tentacles
    this.tentacles = this._createTentacles(2);

    // Gravity pulse
    this.gravPulseInterval = 8000;
    this.gravPulseTimer = 0;
    this.gravPulseActive = false;
    this.gravPulseDuration = 500; // ms
    this.gravPulseElapsed = 0;

    // Tentacle slam
    this.slamInterval = 6000;
    this.slamTimer = 0;
    this.slamActive = false;
    this.slamTentacle = -1;
    this.slamProgress = 0;
  }

  _createTentacles(count) {
    const tents = [];
    const spacing = C.CANVAS_W / (count + 1);
    for (let i = 0; i < count; i++) {
      tents.push({
        baseX: spacing * (i + 1),
        topY: this.eyeY + this.eyeRadius + 10,
        bottomY: 220,
        segments: 8,
        amplitude: 15 + i * 5,
        frequency: 2 + i * 0.3,
        phaseOffset: i * 1.5,
        radius: 5,          // collision radius per segment
        slamOffset: 0,      // extra Y pushed during slam
      });
    }
    return tents;
  }

  onPhaseChange(p) {
    if (p === 2) {
      this.tentacles = this._createTentacles(4);
      this.gravPulseInterval = 8000;
    } else if (p === 3) {
      this.tentacles = this._createTentacles(4);
      this.blinkCycle = 4000;    // 2s open, 2s closed
      this.blinkDuration = 2000;
      this.gravPulseInterval = 5000;
    }
  }

  update(dtMs, balls, paddle) {
    if (!this.alive) return;
    this.elapsed += dtMs;
    if (this.flashTimer > 0) this.flashTimer--;

    // ── Eye blinking ──
    this.blinkTimer += dtMs;
    const fullCycle = this.blinkCycle + this.blinkDuration;
    const cyclePos = this.blinkTimer % fullCycle;
    this.eyeOpen = cyclePos < this.blinkCycle;

    // ── Tentacle slam (phase 2+) ──
    if (this.phase >= 2) {
      this.slamTimer += dtMs;
      if (!this.slamActive && this.slamTimer >= this.slamInterval) {
        this.slamTimer = 0;
        this.slamActive = true;
        this.slamTentacle = Math.floor(Math.random() * this.tentacles.length);
        this.slamProgress = 0;
      }
      if (this.slamActive) {
        this.slamProgress += dtMs / 400; // slam over 400ms
        const tent = this.tentacles[this.slamTentacle];
        if (tent) {
          tent.slamOffset = Math.sin(clamp(this.slamProgress, 0, 1) * Math.PI) * 80;
        }
        if (this.slamProgress >= 1) {
          this.slamActive = false;
          if (this.tentacles[this.slamTentacle]) {
            this.tentacles[this.slamTentacle].slamOffset = 0;
          }
        }
      }
    }

    // ── Gravity pulse (phase 2+) ──
    if (this.phase >= 2) {
      this.gravPulseTimer += dtMs;
      if (!this.gravPulseActive && this.gravPulseTimer >= this.gravPulseInterval) {
        this.gravPulseTimer = 0;
        this.gravPulseActive = true;
        this.gravPulseElapsed = 0;
      }
      if (this.gravPulseActive) {
        this.gravPulseElapsed += dtMs;
        // Pull balls upward
        for (const ball of balls) {
          if (!ball.alive || ball.attached) continue;
          ball.vy -= 0.5;
        }
        if (this.gravPulseElapsed >= this.gravPulseDuration) {
          this.gravPulseActive = false;
        }
      }
    }

    // ── Ball ↔ tentacle collision ──
    const t = this.elapsed / 1000;
    for (const ball of balls) {
      if (!ball.alive || ball.attached) continue;
      for (const tent of this.tentacles) {
        const segH = (tent.bottomY + tent.slamOffset - tent.topY) / tent.segments;
        for (let s = 0; s < tent.segments; s++) {
          const sy = tent.topY + segH * s + segH / 2;
          const sx = tent.baseX +
            Math.sin(t * tent.frequency + s * 0.5 + tent.phaseOffset) *
            tent.amplitude;
          if (circlesOverlap(ball.x, ball.y, ball.radius, sx, sy, tent.radius)) {
            // Reflect ball away from tentacle segment
            const nx = ball.x - sx;
            const ny = ball.y - sy;
            const len = Math.sqrt(nx * nx + ny * ny) || 1;
            const r = reflect(ball.vx, ball.vy, nx / len, ny / len);
            ball.vx = r.vx;
            ball.vy = r.vy;
            ball.x = sx + (nx / len) * (tent.radius + ball.radius + 1);
            ball.y = sy + (ny / len) * (tent.radius + ball.radius + 1);
          }
        }
      }
    }

    this.updateProjectiles();
  }

  checkBallHit(ball) {
    if (!this.alive || ball.attached) return false;
    if (!this.eyeOpen) return false; // invulnerable when closed
    if (circlesOverlap(ball.x, ball.y, ball.radius, this.eyeX, this.eyeY, this.eyeRadius)) {
      this.takeDamage(1);
      const nx = ball.x - this.eyeX;
      const ny = ball.y - this.eyeY;
      const len = Math.sqrt(nx * nx + ny * ny) || 1;
      const r = reflect(ball.vx, ball.vy, nx / len, ny / len);
      ball.vx = r.vx;
      ball.vy = r.vy;
      ball.x = this.eyeX + (nx / len) * (this.eyeRadius + ball.radius + 2);
      ball.y = this.eyeY + (ny / len) * (this.eyeRadius + ball.radius + 2);
      return true;
    }
    return false;
  }

  render(ctx) {
    if (!this.alive) return;
    const t = this.elapsed / 1000;

    // ── Tentacles ──
    for (const tent of this.tentacles) {
      const segH = (tent.bottomY + tent.slamOffset - tent.topY) / tent.segments;
      ctx.strokeStyle = '#00ffaa';
      ctx.lineWidth = 3;
      ctx.shadowColor = '#00ffaa';
      ctx.shadowBlur = 6;
      ctx.beginPath();
      for (let s = 0; s <= tent.segments; s++) {
        const sy = tent.topY + segH * s;
        const sx = tent.baseX +
          Math.sin(t * tent.frequency + s * 0.5 + tent.phaseOffset) *
          tent.amplitude;
        if (s === 0) ctx.moveTo(sx, sy); else ctx.lineTo(sx, sy);
      }
      ctx.stroke();

      // Segment nodes
      for (let s = 0; s < tent.segments; s++) {
        const sy = tent.topY + segH * s + segH / 2;
        const sx = tent.baseX +
          Math.sin(t * tent.frequency + s * 0.5 + tent.phaseOffset) *
          tent.amplitude;
        ctx.beginPath();
        ctx.arc(sx, sy, tent.radius, 0, TWO_PI);
        ctx.fillStyle = '#005544';
        ctx.fill();
        ctx.strokeStyle = '#00ffaa';
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }
    ctx.shadowBlur = 0;

    // ── Eye ──
    // Outer ring
    ctx.beginPath();
    ctx.arc(this.eyeX, this.eyeY, this.eyeRadius, 0, TWO_PI);
    ctx.fillStyle = '#002a22';
    ctx.fill();
    ctx.strokeStyle = '#00cc88';
    ctx.lineWidth = 2;
    ctx.shadowColor = '#00cc88';
    ctx.shadowBlur = 10;
    ctx.stroke();
    ctx.shadowBlur = 0;

    if (this.eyeOpen) {
      // Iris
      ctx.beginPath();
      ctx.arc(this.eyeX, this.eyeY, 14, 0, TWO_PI);
      ctx.fillStyle = '#00ff88';
      ctx.shadowColor = '#00ff88';
      ctx.shadowBlur = 8;
      ctx.fill();
      ctx.shadowBlur = 0;

      // Pupil
      ctx.beginPath();
      ctx.arc(this.eyeX, this.eyeY, 6, 0, TWO_PI);
      ctx.fillStyle = '#000000';
      ctx.fill();
    } else {
      // Closed — horizontal line
      ctx.beginPath();
      ctx.moveTo(this.eyeX - this.eyeRadius + 4, this.eyeY);
      ctx.lineTo(this.eyeX + this.eyeRadius - 4, this.eyeY);
      ctx.strokeStyle = '#00cc88';
      ctx.lineWidth = 3;
      ctx.stroke();
    }

    // Gravity pulse visual
    if (this.gravPulseActive) {
      const pulseAlpha = 0.15 * (1 - this.gravPulseElapsed / this.gravPulseDuration);
      ctx.fillStyle = `rgba(0, 255, 170, ${pulseAlpha})`;
      ctx.fillRect(0, C.HUD_HEIGHT, C.CANVAS_W, C.CANVAS_H - C.HUD_HEIGHT);
    }

    this.renderFlash(ctx, this.eyeX, this.eyeY, this.eyeRadius);
    this.renderProjectiles(ctx);
    this.renderHPBar(ctx);
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// BOSS 3 — THE MELTDOWN ENGINE  (Sector 3, HP 45)
//
// Rectangular reactor with destructible vent weak-points and magma rain.
// Phase 3 exposes a central core and adds a conveyor drift.
// ═════════════════════════════════════════════════════════════════════════════

class MeltdownEngine extends BossBase {
  constructor() {
    super('THE MELTDOWN ENGINE', 45);

    // Reactor body (not directly hittable)
    this.bodyX = (C.CANVAS_W - 200) / 2; // 100
    this.bodyY = 80;
    this.bodyW = 200;
    this.bodyH = 80;

    // Vents — small circle weak-points
    this.vents = this._createVents();

    // Magma drip timer
    this.magmaInterval = 3000;
    this.magmaTimer = 0;
    this.magmaWarnings = []; // {x, timer} — glow zone before drop

    // Phase-3 exposed core
    this.coreExposed = false;
    this.coreX = C.CANVAS_W / 2;
    this.coreY = this.bodyY + this.bodyH / 2;
    this.coreRadius = 20;

    // Conveyor drift (phase 3)
    this.conveyorActive = false;
  }

  _createVents() {
    const cx = C.CANVAS_W / 2;
    const by = this.bodyY || 80;
    const bh = this.bodyH || 80;
    return [
      { x: cx - 70, y: by + 10,      r: 10, alive: true, moving: false, oscBase: 0, oscAmp: 0 },
      { x: cx + 70, y: by + 10,      r: 10, alive: true, moving: false, oscBase: 0, oscAmp: 0 },
      { x: cx - 40, y: by + bh - 10, r: 10, alive: true, moving: false, oscBase: 0, oscAmp: 0 },
      { x: cx + 40, y: by + bh - 10, r: 10, alive: true, moving: false, oscBase: 0, oscAmp: 0 },
    ];
  }

  onPhaseChange(p) {
    if (p === 2) {
      // Seal 2 vents (top two), remaining 2 move
      this.vents[0].alive = false;
      this.vents[1].alive = false;
      this.vents[2].moving = true;
      this.vents[2].oscBase = this.vents[2].x;
      this.vents[2].oscAmp = 40;
      this.vents[3].moving = true;
      this.vents[3].oscBase = this.vents[3].x;
      this.vents[3].oscAmp = 40;
      this.magmaInterval = 2000;
    } else if (p === 3) {
      // All vents sealed; core exposed
      for (const v of this.vents) v.alive = false;
      this.coreExposed = true;
      this.conveyorActive = true;
      this.magmaInterval = 1000;
    }
  }

  update(dtMs, balls, paddle) {
    if (!this.alive) return;
    this.elapsed += dtMs;
    if (this.flashTimer > 0) this.flashTimer--;

    const t = this.elapsed / 1000;

    // Move oscillating vents
    for (const v of this.vents) {
      if (!v.alive || !v.moving) continue;
      v.x = v.oscBase + Math.sin(t * 1.8) * v.oscAmp;
    }

    // Conveyor drift (phase 3) — nudge ball.vx each tick
    if (this.conveyorActive) {
      for (const ball of balls) {
        if (!ball.alive || ball.attached) continue;
        ball.vx += 0.3;
      }
    }

    // Magma drip system
    this.magmaTimer += dtMs;
    if (this.magmaTimer >= this.magmaInterval) {
      this.magmaTimer = 0;
      const wx = 30 + Math.random() * (C.CANVAS_W - 60);
      this.magmaWarnings.push({ x: wx, timer: 600 }); // 600ms warning
    }

    // Process warnings → spawn projectiles when timer expires
    for (let i = this.magmaWarnings.length - 1; i >= 0; i--) {
      this.magmaWarnings[i].timer -= dtMs;
      if (this.magmaWarnings[i].timer <= 0) {
        const mx = this.magmaWarnings[i].x;
        this.projectiles.push({
          x: mx, y: this.bodyY - 10,
          vx: 0, vy: 1.8,
          w: 8, h: 10,
          alive: true,
          color: '#ff6600',
        });
        this.magmaWarnings.splice(i, 1);
      }
    }

    this.updateProjectiles();

    // Ball vs vents / core handled externally via checkBallHit
  }

  checkBallHit(ball) {
    if (!this.alive || ball.attached) return false;

    // Phase 3 — core check
    if (this.coreExposed) {
      if (circlesOverlap(ball.x, ball.y, ball.radius, this.coreX, this.coreY, this.coreRadius)) {
        this.takeDamage(1);
        const nx = ball.x - this.coreX;
        const ny = ball.y - this.coreY;
        const len = Math.sqrt(nx * nx + ny * ny) || 1;
        const r = reflect(ball.vx, ball.vy, nx / len, ny / len);
        ball.vx = r.vx;
        ball.vy = r.vy;
        ball.x = this.coreX + (nx / len) * (this.coreRadius + ball.radius + 2);
        ball.y = this.coreY + (ny / len) * (this.coreRadius + ball.radius + 2);
        return true;
      }
      return false;
    }

    // Vent check
    for (const v of this.vents) {
      if (!v.alive) continue;
      if (circlesOverlap(ball.x, ball.y, ball.radius, v.x, v.y, v.r)) {
        this.takeDamage(1);
        // Bounce ball away
        const nx = ball.x - v.x;
        const ny = ball.y - v.y;
        const len = Math.sqrt(nx * nx + ny * ny) || 1;
        const r = reflect(ball.vx, ball.vy, nx / len, ny / len);
        ball.vx = r.vx;
        ball.vy = r.vy;
        ball.x = v.x + (nx / len) * (v.r + ball.radius + 2);
        ball.y = v.y + (ny / len) * (v.r + ball.radius + 2);
        return true;
      }
    }

    // Ball bounces off reactor body (not a weak-point hit)
    const bx = ball.x, by = ball.y, br = ball.radius;
    if (bx + br > this.bodyX && bx - br < this.bodyX + this.bodyW &&
        by + br > this.bodyY && by - br < this.bodyY + this.bodyH) {
      // Determine which face is closest and reflect
      const overlapL = (bx + br) - this.bodyX;
      const overlapR = (this.bodyX + this.bodyW) - (bx - br);
      const overlapT = (by + br) - this.bodyY;
      const overlapB = (this.bodyY + this.bodyH) - (by - br);
      const minOverlap = Math.min(overlapL, overlapR, overlapT, overlapB);
      if (minOverlap === overlapL) { ball.vx = -Math.abs(ball.vx); ball.x = this.bodyX - br; }
      else if (minOverlap === overlapR) { ball.vx = Math.abs(ball.vx); ball.x = this.bodyX + this.bodyW + br; }
      else if (minOverlap === overlapT) { ball.vy = -Math.abs(ball.vy); ball.y = this.bodyY - br; }
      else { ball.vy = Math.abs(ball.vy); ball.y = this.bodyY + this.bodyH + br; }
    }

    return false;
  }

  render(ctx) {
    if (!this.alive) return;

    // ── Reactor body ──
    ctx.fillStyle = '#1a0800';
    ctx.strokeStyle = '#ff6600';
    ctx.lineWidth = 2;
    ctx.shadowColor = '#ff6600';
    ctx.shadowBlur = 8;
    ctx.fillRect(this.bodyX, this.bodyY, this.bodyW, this.bodyH);
    ctx.strokeRect(this.bodyX, this.bodyY, this.bodyW, this.bodyH);
    ctx.shadowBlur = 0;

    // Internal glow lines
    for (let i = 1; i < 4; i++) {
      const lx = this.bodyX + (this.bodyW / 4) * i;
      ctx.beginPath();
      ctx.moveTo(lx, this.bodyY + 5);
      ctx.lineTo(lx, this.bodyY + this.bodyH - 5);
      ctx.strokeStyle = 'rgba(255, 100, 0, 0.25)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // ── Active vents ──
    for (const v of this.vents) {
      if (!v.alive) continue;
      ctx.beginPath();
      ctx.arc(v.x, v.y, v.r, 0, TWO_PI);
      ctx.fillStyle = '#ff8800';
      ctx.shadowColor = '#ff8800';
      ctx.shadowBlur = 10;
      ctx.fill();
      // Inner ring
      ctx.beginPath();
      ctx.arc(v.x, v.y, v.r * 0.5, 0, TWO_PI);
      ctx.fillStyle = '#ffcc00';
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    // ── Phase-3 exposed core ──
    if (this.coreExposed) {
      ctx.beginPath();
      ctx.arc(this.coreX, this.coreY, this.coreRadius, 0, TWO_PI);
      ctx.fillStyle = '#ff2200';
      ctx.shadowColor = '#ff4400';
      ctx.shadowBlur = 15;
      ctx.fill();
      ctx.shadowBlur = 0;
      // Pulsing inner core
      const pulse = 0.6 + Math.sin(this.elapsed / 200) * 0.4;
      ctx.beginPath();
      ctx.arc(this.coreX, this.coreY, this.coreRadius * 0.5 * pulse, 0, TWO_PI);
      ctx.fillStyle = '#ffaa00';
      ctx.fill();
    }

    // ── Magma warnings (glow zones) ──
    for (const w of this.magmaWarnings) {
      const alpha = 0.3 + 0.3 * Math.sin(this.elapsed / 80);
      ctx.fillStyle = `rgba(255, 100, 0, ${alpha})`;
      ctx.fillRect(w.x - 6, this.bodyY - 20, 12, 20);
    }

    this.renderFlash(ctx, this.coreX, this.coreY, this.coreExposed ? this.coreRadius : 30);
    this.renderProjectiles(ctx);
    this.renderHPBar(ctx);
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// BOSS 4 — THE VOID ARCHITECT  (Sector 4, HP 50)
//
// Morphing polygon (triangle→square→pentagon) with portal warp pairs
// and dead zones. Purple/cosmic aesthetic.
// ═════════════════════════════════════════════════════════════════════════════

class VoidArchitect extends BossBase {
  constructor() {
    super('THE VOID ARCHITECT', 50);

    // Center position
    this.cx = C.CANVAS_W / 2;
    this.cy = 120;
    this.polyRadius = 40;
    this.polyAngle = 0;       // current rotation
    this.polySides = 3;       // triangle initially

    // Morph — smoothly interpolate sides between phases
    this.morphSides = 3;      // floating point for smooth visual transition
    this.morphTarget = 3;

    // Weak-point (center)
    this.weakRadius = 20;

    // Portal pairs — each pair: [{x,y}, {x,y}]
    this.portals = [this._randomPortalPair()];
    this.portalRepoInterval = 8000;
    this.portalRepoTimer = 0;
    this.portalRadius = 15;

    // Dead zones — {x, y, w, h}
    this.deadZones = [];

    // Phase-3 projectile timer
    this.projInterval = 3000;
    this.projTimer = 0;
  }

  /** Generate a random portal pair in the play area (y 180–550). */
  _randomPortalPair() {
    return [
      { x: 40 + Math.random() * 100,  y: 180 + Math.random() * 250 },
      { x: 260 + Math.random() * 100, y: 250 + Math.random() * 250 },
    ];
  }

  /** Generate a random dead zone rectangle. */
  _randomDeadZone() {
    return {
      x: 30 + Math.random() * (C.CANVAS_W - 90),
      y: 200 + Math.random() * 200,
      w: 40 + Math.random() * 40,
      h: 20 + Math.random() * 30,
    };
  }

  onPhaseChange(p) {
    if (p === 2) {
      this.morphTarget = 4; // square
      this.portalRepoInterval = 6000;
      this.deadZones = [this._randomDeadZone(), this._randomDeadZone()];
    } else if (p === 3) {
      this.morphTarget = 5; // pentagon
      this.portals = [this._randomPortalPair(), this._randomPortalPair()];
      this.deadZones = [this._randomDeadZone(), this._randomDeadZone(), this._randomDeadZone()];
      this.projInterval = 2500;
    }
  }

  update(dtMs, balls, paddle) {
    if (!this.alive) return;
    this.elapsed += dtMs;
    if (this.flashTimer > 0) this.flashTimer--;

    const dtSec = dtMs / 1000;

    // Polygon rotation
    this.polyAngle += 0.4 * dtSec;

    // Morph sides smoothly toward target
    if (this.morphSides < this.morphTarget) {
      this.morphSides = Math.min(this.morphSides + dtSec * 0.8, this.morphTarget);
    }
    this.polySides = Math.round(this.morphSides);

    // Portal repositioning timer
    this.portalRepoTimer += dtMs;
    if (this.portalRepoTimer >= this.portalRepoInterval) {
      this.portalRepoTimer = 0;
      for (let i = 0; i < this.portals.length; i++) {
        this.portals[i] = this._randomPortalPair();
      }
    }

    // Portal teleportation
    for (const ball of balls) {
      if (!ball.alive || ball.attached) continue;
      for (const pair of this.portals) {
        this._checkPortal(ball, pair[0], pair[1]);
        this._checkPortal(ball, pair[1], pair[0]);
      }
    }

    // Phase-3 projectiles
    if (this.phase === 3) {
      this.projTimer += dtMs;
      if (this.projTimer >= this.projInterval) {
        this.projTimer = 0;
        this._fireVoidProjectiles();
      }
    }

    this.updateProjectiles();
  }

  /** Teleport ball from portal A to portal B if within range. */
  _checkPortal(ball, from, to) {
    const d2 = dist2(ball.x, ball.y, from.x, from.y);
    if (d2 < this.portalRadius * this.portalRadius) {
      // Teleport to the paired portal
      ball.x = to.x;
      ball.y = to.y;
      // Offset in travel direction to prevent re-trigger
      const speed = Math.sqrt(ball.vx * ball.vx + ball.vy * ball.vy) || 1;
      ball.x += (ball.vx / speed) * (this.portalRadius + 5);
      ball.y += (ball.vy / speed) * (this.portalRadius + 5);
    }
  }

  _fireVoidProjectiles() {
    // Radial burst from polygon center
    const count = 5;
    for (let i = 0; i < count; i++) {
      const a = (TWO_PI / count) * i + this.polyAngle;
      this.projectiles.push({
        x: this.cx, y: this.cy,
        vx: Math.cos(a) * 1.2,
        vy: Math.sin(a) * 1.2,
        w: 5, h: 5,
        alive: true,
        color: '#aa44ff',
      });
    }
  }

  checkBallHit(ball) {
    if (!this.alive || ball.attached) return false;

    // Check if ball is inside a dead zone (collision disabled there)
    for (const dz of this.deadZones) {
      if (ball.x > dz.x && ball.x < dz.x + dz.w &&
          ball.y > dz.y && ball.y < dz.y + dz.h) {
        return false;
      }
    }

    // Center weak-point
    if (circlesOverlap(ball.x, ball.y, ball.radius, this.cx, this.cy, this.weakRadius)) {
      this.takeDamage(1);
      const nx = ball.x - this.cx;
      const ny = ball.y - this.cy;
      const len = Math.sqrt(nx * nx + ny * ny) || 1;
      const r = reflect(ball.vx, ball.vy, nx / len, ny / len);
      ball.vx = r.vx;
      ball.vy = r.vy;
      ball.x = this.cx + (nx / len) * (this.weakRadius + ball.radius + 2);
      ball.y = this.cy + (ny / len) * (this.weakRadius + ball.radius + 2);
      return true;
    }

    // Bounce off polygon perimeter (approximate as circle at polyRadius)
    if (circlesOverlap(ball.x, ball.y, ball.radius, this.cx, this.cy, this.polyRadius) &&
        !circlesOverlap(ball.x, ball.y, ball.radius, this.cx, this.cy, this.weakRadius)) {
      const nx = ball.x - this.cx;
      const ny = ball.y - this.cy;
      const len = Math.sqrt(nx * nx + ny * ny) || 1;
      const r = reflect(ball.vx, ball.vy, nx / len, ny / len);
      ball.vx = r.vx;
      ball.vy = r.vy;
      ball.x = this.cx + (nx / len) * (this.polyRadius + ball.radius + 2);
      ball.y = this.cy + (ny / len) * (this.polyRadius + ball.radius + 2);
    }

    return false;
  }

  render(ctx) {
    if (!this.alive) return;

    // ── Dead zones — glitchy rectangles ──
    for (const dz of this.deadZones) {
      ctx.fillStyle = 'rgba(100, 0, 180, 0.12)';
      ctx.fillRect(dz.x, dz.y, dz.w, dz.h);
      // Static noise lines
      ctx.strokeStyle = 'rgba(180, 80, 255, 0.25)';
      ctx.lineWidth = 1;
      for (let i = 0; i < 3; i++) {
        const ly = dz.y + Math.random() * dz.h;
        ctx.beginPath();
        ctx.moveTo(dz.x, ly);
        ctx.lineTo(dz.x + dz.w, ly + (Math.random() - 0.5) * 4);
        ctx.stroke();
      }
      // Border
      ctx.strokeStyle = 'rgba(170, 0, 255, 0.35)';
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(dz.x, dz.y, dz.w, dz.h);
      ctx.setLineDash([]);
    }

    // ── Portals ──
    for (const pair of this.portals) {
      for (let pi = 0; pi < 2; pi++) {
        const p = pair[pi];
        const pulse = 10 + Math.sin(this.elapsed / 300 + pi * Math.PI) * 4;
        // Outer glow
        ctx.beginPath();
        ctx.arc(p.x, p.y, pulse, 0, TWO_PI);
        ctx.fillStyle = 'rgba(170, 0, 255, 0.15)';
        ctx.fill();
        // Core
        ctx.beginPath();
        ctx.arc(p.x, p.y, this.portalRadius * 0.6, 0, TWO_PI);
        ctx.fillStyle = '#6600cc';
        ctx.shadowColor = '#aa00ff';
        ctx.shadowBlur = 12;
        ctx.fill();
        ctx.shadowBlur = 0;
        // Ring
        ctx.beginPath();
        ctx.arc(p.x, p.y, this.portalRadius, 0, TWO_PI);
        ctx.strokeStyle = '#cc44ff';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
      // Connecting line between pair
      ctx.beginPath();
      ctx.moveTo(pair[0].x, pair[0].y);
      ctx.lineTo(pair[1].x, pair[1].y);
      ctx.strokeStyle = 'rgba(170, 0, 255, 0.1)';
      ctx.lineWidth = 1;
      ctx.setLineDash([6, 8]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // ── Polygon body ──
    ctx.save();
    ctx.translate(this.cx, this.cy);
    ctx.rotate(this.polyAngle);

    // Outer polygon
    ctx.beginPath();
    for (let i = 0; i < this.polySides; i++) {
      const a = (TWO_PI / this.polySides) * i;
      const px = Math.cos(a) * this.polyRadius;
      const py = Math.sin(a) * this.polyRadius;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fillStyle = '#0d001a';
    ctx.fill();
    ctx.strokeStyle = '#aa00ff';
    ctx.lineWidth = 2;
    ctx.shadowColor = '#aa00ff';
    ctx.shadowBlur = 12;
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Weak-point center glow
    const pulse = 0.5 + Math.sin(this.elapsed / 400) * 0.3;
    ctx.beginPath();
    ctx.arc(0, 0, this.weakRadius * pulse, 0, TWO_PI);
    ctx.fillStyle = 'rgba(170, 0, 255, 0.3)';
    ctx.fill();
    ctx.restore();

    this.renderFlash(ctx, this.cx, this.cy, this.polyRadius);
    this.renderProjectiles(ctx);
    this.renderHPBar(ctx);
  }
}

// ─── Factory ─────────────────────────────────────────────────────────────────

/**
 * Create the boss instance for a given sector (1-4).
 * @param {number} sectorIndex – 1-based sector number.
 * @returns {BossBase} The boss instance.
 */
export function createBoss(sectorIndex) {
  switch (sectorIndex) {
    case 1: return new HexCore();
    case 2: return new CyberKraken();
    case 3: return new MeltdownEngine();
    case 4: return new VoidArchitect();
    default:
      console.warn(`Boss: unknown sector ${sectorIndex}, defaulting to HexCore`);
      return new HexCore();
  }
}

// Export classes for direct use / testing
export { BossBase, HexCore, CyberKraken, MeltdownEngine, VoidArchitect };
